import { ReactElement, useMemo, useState } from 'react';
import styles from './user-email-account-validation.module.scss';
import DashboardMemberTransferEmailView from '@utmb/types/view/DashboardMemberTransferEmailView';
import { Button } from '@utmb/design-system/button/Button';
import { getColorStyle } from '@utmb/utils/colors';
import { ColorTypesEnum } from '@utmb/types/colors';
import { useUser } from '@utmb/contexts/AuthContext';
import { Font } from '@utmb/design-system/text/font';
import { FetchMethodsEnum, fetchTheAPI } from '@utmb/utils/utmb';
import { useTranslation } from 'next-i18next';
import { UserMemberTransferStatusEnum } from '@utmb/types/enum/UserMemberTransferStatusEnum';
import { Skeleton } from '@utmb/design-system/placeholder/Placeholder';

export interface UserEmailAcountValidationProps {
    closeCallback: () => void;
    transfer: DashboardMemberTransferEmailView;
}

export const UserEmailAcountValidation = ({ closeCallback, transfer }: UserEmailAcountValidationProps): ReactElement => {
    const { t } = useTranslation();
    const user = useUser();
    const [frontStatus, setFrontStatus] = useState(StatusEnum.INITIAL);
    const [isLoading, setIsLoading] = useState(false);
    const message = useMemo(() => {
        if (frontStatus === StatusEnum.INITIAL) {
            if (transfer.state === UserMemberTransferStatusEnum.EMAIL) {
                return t('myUtmb.email.response.email', { email: transfer.newEmail });
            } else if (transfer.state === UserMemberTransferStatusEnum.HUMAN) {
                if (user.me!.email === transfer.newEmail) {
                    return t('myUtmb.email.response.humanNotification', { old: transfer.oldEmail, new: transfer.newEmail });
                } else {
                    return t('myUtmb.email.response.human', { email: transfer.newEmail });
                }
            } else if (transfer.state === UserMemberTransferStatusEnum.REQUESTED) {
                if (user.me!.email !== transfer.newEmail) return `${transfer.newEmail} ${t('myUtmb.email.response.requested')}`;
                else return t('myUtmb.email.response.confirmationAsk', { old: transfer.oldEmail, new: transfer.newEmail });
            }
        } else if (frontStatus === StatusEnum.CONFIRM) {
            return t('myUtmb.email.response.confirmed');
        } else {
            return t('myUtmb.email.response.cancelled');
        }
    }, [frontStatus]);

    async function handleCancelClick(): Promise<void> {
        if (!isLoading) {
            const token = await user.getToken();

            fetchTheAPI({
                isClient: true,
                method: FetchMethodsEnum.DELETE,
                path: `/users/dashboard/member/cancel-email-request`,
                bearer: token,
                body: { email: user.me!.email === transfer.newEmail ? transfer.oldEmail : transfer.newEmail },
            })
                .then(() => {
                    setFrontStatus(StatusEnum.CANCEL);
                })
                .catch((error) => {
                    alert(error);
                });
        }
    }

    async function handleConfirmationClick(): Promise<void> {
        if (!isLoading) {
            setIsLoading(true);
            const token = await user.getToken();

            fetchTheAPI({
                isClient: true,
                method: FetchMethodsEnum.POST,
                path: `/users/dashboard/member/change-email-validation`,
                bearer: token,
                body: { email: transfer.oldEmail },
            })
                .then(() => {
                    setIsLoading(false);
                    setFrontStatus(StatusEnum.CONFIRM);
                })
                .catch((error) => {
                    setIsLoading(false);
                    alert(error);
                });
        }
    }

    return (
        <div className={styles.email_account_validation}>
            <div className={styles.wrap_text}>
                {isLoading ? (
                    <Skeleton height="60" width="100%" />
                ) : (
                    <Font.FuturaBook mobile="22" desktop="22" as="span">
                        {message}
                    </Font.FuturaBook>
                )}
            </div>

            <div className={styles.wrap_button}>
                {frontStatus === StatusEnum.INITIAL ? (
                    <>
                        <Button.Default
                            className={styles.button}
                            color={getColorStyle(ColorTypesEnum.WHITE)}
                            bgcolor={getColorStyle(ColorTypesEnum.RED)}
                            type="button"
                            label={'Annuler'}
                            onPress={handleCancelClick}
                            disabled={isLoading ? true : false}
                        />
                        {transfer.oldEmail !== user.me!.email &&
                        frontStatus === StatusEnum.INITIAL &&
                        transfer.state === UserMemberTransferStatusEnum.REQUESTED ? (
                            <Button.Default
                                className={styles.button}
                                color={getColorStyle(ColorTypesEnum.WHITE)}
                                bgcolor={getColorStyle(ColorTypesEnum.PRIMARY)}
                                type="button"
                                label={'Confirmer'}
                                onPress={handleConfirmationClick}
                                disabled={isLoading ? true : false}
                            />
                        ) : null}
                    </>
                ) : (
                    <Button.Default
                        className={styles.button}
                        color={getColorStyle(ColorTypesEnum.WHITE)}
                        bgcolor={getColorStyle(ColorTypesEnum.PRIMARY)}
                        type="button"
                        label={'Fermer'}
                        onPress={closeCallback}
                    />
                )}
            </div>
        </div>
    );
};

export enum StatusEnum {
    INITIAL = 'initial',
    CONFIRM = 'confirm',
    CANCEL = 'cancel',
}
